import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Wand2, Loader2, Film, Music, Shuffle, Layers, Gauge, Piano, Type as TypeIcon } from "lucide-react";
import { musicApi, errMsg, fmtTime } from "@/lib/musicApi";
import { ReferenceInput } from "@/components/music/ReferenceInput";

const MODES = [
  { k: "text", label: "Text to music", sub: "Describe it, get an original track", icon: TypeIcon },
  { k: "cinematic", label: "Cinematic score", sub: "Film / trailer background", icon: Film },
  { k: "instrumental", label: "Instrumental version", sub: "New instrumental in the reference's style", icon: Music, ref: true },
  { k: "similar", label: "Similar mood, new melody", sub: "Same vibe, original tune", icon: Shuffle, ref: true },
  { k: "arrangement", label: "Alternative arrangement", sub: "Fresh orchestration", icon: Layers, ref: true },
  { k: "style", label: "Style version", sub: "Acoustic, orchestral, piano…", icon: Piano, ref: true },
  { k: "tempo", label: "Tempo & energy", sub: "Re-imagined speed and drive", icon: Gauge, ref: true },
];
const STYLES = ["acoustic", "orchestral", "piano", "electronic", "ambient"];
const GENRES = ["Lo-fi", "Cinematic", "Pop", "Hip hop", "EDM", "Rock", "Jazz", "Classical", "Indian classical", "Synthwave", "Ambient", "Folk"];
const MOODS = ["Uplifting", "Calm", "Dark", "Epic", "Romantic", "Sad", "Playful", "Tense", "Dreamy"];
const INSTRUMENTS = ["Piano", "Strings", "Guitar", "Synth", "Drums", "Bass", "Flute", "Brass", "Choir pads", "Sitar", "Tabla", "Violin"];

const Label = ({ children, right }) => <div className="mb-2 flex items-center justify-between text-[11px] font-bold uppercase tracking-[0.14em] text-white/45"><span>{children}</span>{right}</div>;
const Pill = ({ on, children, ...p }) => (
  <button {...p} className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${on ? "border-[#C8FF4D] bg-[#C8FF4D] text-black" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"}`}>{children}</button>
);

export const CreatePanel = ({ config, projectId, onCreated }) => {
  const providers = config.providers;
  const [f, setF] = useState({ title: "", provider: config.settings.default_provider, mode: "text", prompt: "", style: "orchestral", genre: "", mood: "", instruments: [], tempo_bpm: "", intensity: 50, duration: 60, strength: 0.75 });
  const [refs, setRefs] = useState([]);
  const [refId, setRefId] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const prov = providers.find((p) => p.key === f.provider) || providers[0];
  const mode = MODES.find((m) => m.k === f.mode);
  const needsRef = mode.ref || prov.needs_reference;
  const cap = Math.min(prov.max_seconds, config.settings.max_duration);

  useEffect(() => { musicApi.references().then(setRefs).catch(() => {}); }, []);
  useEffect(() => { if (f.duration > cap) set("duration", cap); }, [cap]); // eslint-disable-line react-hooks/exhaustive-deps
  const cost = useMemo(() => prov.usd_per_minute ? `≈ $${(prov.usd_per_minute * f.duration / 60).toFixed(2)}` : prov.usd_per_audio ? `≈ $${prov.usd_per_audio.toFixed(2)}` : "Your ElevenLabs credits", [prov, f.duration]);

  const generate = async () => {
    if (needsRef && !refId) { toast.error("Add a reference first — upload audio or paste a YouTube link"); return; }
    if (f.mode === "text" && !f.prompt.trim()) { toast.error("Describe the music you want"); return; }
    setBusy(true);
    try {
      const t = await musicApi.generate({ ...f, title: f.title || (f.prompt.slice(0, 40) || mode.label), project_id: projectId, reference_id: refId,
        tempo_bpm: f.tempo_bpm ? Number(f.tempo_bpm) : null, genre: f.genre || null, mood: f.mood || null });
      toast.success("Generating — you can keep working"); onCreated(t);
    } catch (e) { toast.error(errMsg(e, "Could not start generation")); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="music-create-panel">
      <section>
        <Label>Engine</Label>
        <div className="grid gap-2 sm:grid-cols-3">
          {providers.map((p) => (
            <button key={p.key} onClick={() => p.configured && set("provider", p.key)} disabled={!p.configured} data-testid={`engine-${p.key}`}
              className={`rounded-xl border p-3 text-left transition-colors disabled:opacity-40 ${f.provider === p.key ? "border-[#C8FF4D] bg-[#C8FF4D]/[0.07]" : "border-white/10 hover:border-white/25"}`}>
              <div className="text-[13px] font-bold text-white">{p.label}</div>
              <div className="mt-1 text-[11px] leading-snug text-white/45">{p.configured ? p.note : "Not configured — add the API key"}</div>
              <div className="mt-1.5 font-mono text-[10px] text-[#C8FF4D]/80">max {fmtTime(p.max_seconds)}</div>
            </button>
          ))}
        </div>
      </section>
      <section>
        <Label>Output</Label>
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {MODES.map((m) => (
            <button key={m.k} onClick={() => set("mode", m.k)} data-testid={`mode-${m.k}`}
              className={`group rounded-xl border p-3 text-left transition-colors ${f.mode === m.k ? "border-[#C8FF4D] bg-[#C8FF4D]/[0.07]" : "border-white/10 hover:border-white/25"}`}>
              <m.icon size={16} className={f.mode === m.k ? "text-[#C8FF4D]" : "text-white/50"} />
              <div className="mt-2 text-[12.5px] font-bold text-white">{m.label}</div>
              <div className="text-[10.5px] text-white/40">{m.sub}{m.ref ? " · needs reference" : ""}</div>
            </button>
          ))}
        </div>
        {f.mode === "style" && <div className="mt-3 flex flex-wrap gap-1.5">{STYLES.map((s) => <Pill key={s} on={f.style === s} onClick={() => set("style", s)} data-testid={`style-${s}`}>{s}</Pill>)}</div>}
      </section>
      {(needsRef || refId) && (
        <section><Label right={!needsRef && <span className="normal-case tracking-normal text-white/30">optional</span>}>Reference</Label>
          <ReferenceInput refs={refs} setRefs={setRefs} selectedId={refId} onSelect={setRefId} />
        </section>
      )}
      <section>
        <Label>Prompt</Label>
        <textarea value={f.prompt} onChange={(e) => set("prompt", e.target.value)} rows={3} maxLength={1200} data-testid="music-prompt"
          placeholder="e.g. Warm lo-fi beat with dusty vinyl crackle, mellow Rhodes chords and a slow head-nod groove"
          className="w-full resize-none rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white outline-none placeholder:text-white/25 focus:border-[#C8FF4D]/60" />
        <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Track title (optional)" maxLength={80} data-testid="music-title"
          className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-white outline-none placeholder:text-white/25 focus:border-[#C8FF4D]/60" />
      </section>
      <section className="grid gap-5 md:grid-cols-2">
        <div><Label>Genre</Label><div className="flex flex-wrap gap-1.5">{GENRES.map((g) => <Pill key={g} on={f.genre === g} onClick={() => set("genre", f.genre === g ? "" : g)} data-testid={`genre-${g}`}>{g}</Pill>)}</div></div>
        <div><Label>Mood</Label><div className="flex flex-wrap gap-1.5">{MOODS.map((g) => <Pill key={g} on={f.mood === g} onClick={() => set("mood", f.mood === g ? "" : g)} data-testid={`mood-${g}`}>{g}</Pill>)}</div></div>
      </section>
      <section>
        <Label>Instruments</Label>
        <div className="flex flex-wrap gap-1.5">{INSTRUMENTS.map((g) => { const on = f.instruments.includes(g); return <Pill key={g} on={on} data-testid={`instrument-${g}`} onClick={() => set("instruments", on ? f.instruments.filter((x) => x !== g) : [...f.instruments, g])}>{g}</Pill>; })}</div>
      </section>
      <section className="grid gap-5 md:grid-cols-3">
        <Slider label="Duration" value={f.duration} min={10} max={cap} step={5} fmt={fmtTime} onChange={(v) => set("duration", v)} testId="music-duration" />
        <Slider label="Intensity" value={f.intensity} min={0} max={100} step={5} fmt={(v) => `${v}%`} onChange={(v) => set("intensity", v)} testId="music-intensity" />
        <div><Label>Tempo (BPM)</Label><input type="number" min={40} max={220} value={f.tempo_bpm} onChange={(e) => set("tempo_bpm", e.target.value)} placeholder={needsRef ? "Auto from reference" : "Auto"} data-testid="music-tempo"
          className="w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 font-mono text-sm text-white outline-none focus:border-[#C8FF4D]/60" /></div>
        {prov.needs_reference && <Slider label="Change vs reference" value={f.strength} min={0.1} max={1} step={0.05} fmt={(v) => `${Math.round(v * 100)}%`} onChange={(v) => set("strength", v)} testId="music-strength" />}
      </section>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <div className="text-xs text-white/50"><span className="font-mono text-white">{cost}</span> · {fmtTime(f.duration)} · {prov.label}</div>
        <button onClick={generate} disabled={busy} data-testid="music-generate-btn"
          className="flex items-center gap-2 rounded-full bg-[#C8FF4D] px-6 py-3 text-sm font-black text-black shadow-[0_0_40px_-8px_#C8FF4D] transition-transform hover:scale-[1.03] active:scale-95 disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />} Generate
        </button>
      </div>
      {!needsRef && !refId && (
        <button onClick={() => set("mode", "similar")} data-testid="music-add-reference" className="text-xs font-semibold text-[#7AD7FF] hover:underline">
          + Use a reference song (upload or YouTube)
        </button>
      )}
    </div>
  );
};

const Slider = ({ label, value, min, max, step, fmt, onChange, testId }) => (
  <div>
    <Label right={<span className="font-mono normal-case tracking-normal text-[#C8FF4D]">{fmt(value)}</span>}>{label}</Label>
    <input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(Number(e.target.value))} data-testid={testId} className="w-full accent-[#C8FF4D]" />
  </div>
);
